<script lang="ts" setup>
import NavigationMenuSignin from '~/assets/icons/app/navigationMenuSignin.png'
import NavigationMenuSignup from '~/assets/icons/app/NavigationMenuSignup.png'
import NavigationMenuNotification from '~/assets/icons/app/NavigationMenuNotification.png'
import NavigationMenuNotificationWhite from '~/assets/icons/app/NavigationMenuNotificationWhite.png'
import NavigationMenuProfile from '~/assets/icons/app/NavigationMenuProfile.png'
import NavigationMenuSubscription from '~/assets/icons/app/NavigationMenuSubscription.png'
import NavigationMenuLogOut from '~/assets/icons/app/NavigationMenuLogOut.png'
import Help from '~/assets/icons/app/help.png'

const authStore = useAuthStore()
const loginStore = useLoginStore()
const notificationStore = useNotificationStore()
const myProfileStore = useMyProfileStore()
const route = useRoute()
const router = useRouter()

const isLogin = computed(() => authStore.isAuthenticated)
const userCredits = computed(() => authStore.credits)
const formattedCredits = computed(() => userCredits.value.toLocaleString('en-US'))
const unreadNotificationCount = computed(() => notificationStore.unreadCount)
const isOnNotificationPage = computed(() => route.path === '/notification')

const handleSignIn = () => {
  loginStore.openLoginModal('SignIn')
}

const handleSignUp = () => {
  loginStore.openLoginModal('SignUpVerifyCode')
}

const goSubscribe = () => {
  if (!isLogin.value) {
    loginStore.openLoginModal('SignIn')
    return
  }
  router.push('/subscribe')
}

const goNotification = () => {
  if (!isLogin.value) {
    loginStore.openLoginModal('SignIn')
    return
  }
  router.push('/notification')
}

const openHelp = () => {
  window.open('https://xmusio.tawk.help/', '_blank', 'noopener')
}

const handleLogout = () => {
  authStore.logout()
}
</script>

<template>
  <header class="fixed top-0 left-0 right-0 z-40 md:hidden bg-[#131318]">
    <div class="flex items-center justify-between py-[10px] px-[16px]">
      <div class="text-[16px] leading-[44px] text-[#E7E7EC]">
        XMUSIO
      </div>

      <!-- 未登录 -->
      <div
        v-if="!isLogin"
        class="flex items-center gap-[12px]"
      >
        <button
          class="flex items-center justify-center py-[9px] px-[8px] rounded-[8px] border border-[#E7E7EC] bg-transparent"
          @click="handleSignIn"
        >
          <img
            class="size-[24px] p-[4px]"
            :src="NavigationMenuSignin"
            alt=""
          >
          <span class="text-[14px] text-[#E7E7EC] leading-[24px]">Sign In</span>
        </button>
        <button
          class="flex items-center justify-center bg-[#E7E7EC] py-[10px] px-[8px] rounded-[8px]"
          @click="handleSignUp"
        >
          <img
            class="size-[24px] p-[4px]"
            :src="NavigationMenuSignup"
            alt=""
          >
          <span class="text-[14px] text-[#1E1E1E] leading-[24px]">Sign Up</span>
        </button>
      </div>

      <!-- 已登录 -->
      <div
        v-else
        class="flex items-center gap-[32px]"
      >
        <!-- credits -->
        <button
          class="flex flex-col items-center justify-center py-[7px] px-[11px] rounded-[8px] border border-[#383847] bg-transparent"
          @click="goSubscribe"
        >
          <span class="text-[12px] leading-[12px] text-[#D880FF]">
            {{ formattedCredits }}
          </span>
          <span class="text-[12px] leading-[16px] text-[#85859F]">
            Credits
          </span>
        </button>

        <!-- notification -->
        <button
          :class="`flex items-center justify-center p-[13px] rounded-[8px] border border-[#383847] bg-transparent ${isOnNotificationPage && 'bg-[#383847]!'}`"
          @click="goNotification"
        >
          <div class="relative">
            <img
              class="size-[16px]"
              :src="isOnNotificationPage ? NavigationMenuNotificationWhite : NavigationMenuNotification"
              alt=""
            >
            <span
              v-if="unreadNotificationCount > 0"
              class="absolute -top-[12px] -right-[12px] flex items-center justify-center size-[16px] text-[10px] leading-[16px] text-white bg-[#B3261E] px-[3px] rounded-full"
            >
              {{ unreadNotificationCount > 99 ? '99+' : unreadNotificationCount }}
            </span>
          </div>
        </button>
        <UPopover
          mode="click"
          :key="authStore.avatarUrl"
          :content="{ side: 'bottom', align: 'start', sideOffset: 10 }"
          :ui="{
            content: 'bg-[#383847] border border-[#5E5E77] rounded-[8px] ring-0'
          }"
        >
          <!-- avatar -->
          <div
            class="size-[44px] rounded-full overflow-hidden"
          >
            <UAvatar
              v-if="authStore.avatarUrl"
              class="size-full"
              :src="authStore.avatarUrl"
            />
            <img v-else class="size-full" src="https://copyright.bdstatic.com/vcg/creative/d90a05ca26b2ca79dc1cbaa4931b18ee.jpg@wm_1,k_cGljX2JqaHdhdGVyLmpwZw==" alt="" srcset="">
          </div>
          <template #content>
            <div
              class="flex items-center gap-x-[8px] px-[12px] py-[6px] hover:bg-[#262635] cursor-pointer rounded-t-[8px]"
              @click="myProfileStore.openMyProfileModal()"
            >
              <img
                class="size-[20px] md:size-[16px]"
                :src="NavigationMenuProfile"
                alt=""
              >
              <p class="text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px]">
                My Profile
              </p>
            </div>
            <NuxtLink to="/subscribe">
              <div class="flex items-center gap-x-[8px] px-[12px] py-[6px] hover:bg-[#262635] cursor-pointer">
                <img
                  class="size-[20px] md:size-[16px]"
                  :src="NavigationMenuSubscription"
                  alt=""
                >
                <p class="text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px]">
                  Manage Subscription
                </p>
              </div>
            </NuxtLink>
            <div
              class="flex items-center gap-x-[8px] px-[12px] py-[6px] hover:bg-[#262635] cursor-pointer rounded-b-[8px]"
              @click="openHelp"
            >
              <img
                class="size-[20px] md:size-[16px]"
                :src="Help"
                alt=""
              >
              <p
                class="text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px]"
              >
                Help
              </p>
            </div>
            <div
              class="flex items-center gap-x-[8px] px-[12px] py-[6px] hover:bg-[#262635] cursor-pointer rounded-b-[8px]"
              @click="handleLogout"
            >
              <img
                class="size-[20px] md:size-[16px]"
                :src="NavigationMenuLogOut"
                alt=""
              >
              <p class="text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px]">
                Log Out
              </p>
            </div>
          </template>
        </UPopover>
      </div>
    </div>
  </header>
</template>
